import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="statement-secondary-packaging-line-items--form"
export default class extends Controller {
  static targets = [
    "totalPrice",
    "entriesContainer",
    "entryTemplate",
    "entryRow",
    "unitsCount",
    "pricePerGram",
    "availableUnits",
    "destroyField",
    "currency",
    "entryTotal",
    // Breakdown under total price
    "summaryContent",
    "summaryTotalWeight",
    // Per-entry summary targets
    "entrySummary",
    "entrySummaryUnits",
    "entrySummaryPackaging",
    "entrySummaryWeightPerUnit",
    "entrySummaryVariety",
    "entrySummaryTotalWeight",
    "entrySummaryPrice",
  ];

  static values = { showPrices: { type: Boolean, default: true } };

  connect() {
    this.updateTotals();
    this.updateAllAvailableUnits();
    this.disableSelectedStocks();
  }

  addEntry() {
    const index = new Date().getTime();
    const templateContent = this.entryTemplateTarget.innerHTML;
    const newRow = templateContent.replaceAll("NEW_INDEX", index);
    this.entriesContainerTarget.insertAdjacentHTML("beforeend", newRow);
    this.disableSelectedStocks();
  }

  removeEntry(event) {
    const visibleRows = this.entryRowTargets.filter(
      (r) => !r.classList.contains("hidden"),
    );
    if (visibleRows.length <= 1) return;

    const row = event.target.closest(
      "[data-statement-secondary-packaging-line-items--form-target='entryRow']",
    );
    if (!row) return;

    const destroyField = row.querySelector(
      "[data-statement-secondary-packaging-line-items--form-target='destroyField']",
    );

    if (destroyField) {
      destroyField.value = "1";
      row.classList.add("hidden");
    } else {
      row.remove();
    }

    this.disableSelectedStocks();
    this.updateTotals();
  }

  updateTotals() {
    const currencyCode = this.currencyTarget?.value || "COP";
    const symbol = this.currencySymbol(currencyCode);
    let grandTotal = 0;
    let totalWeight = 0;
    let hasValidEntries = false;

    this.entryRowTargets.forEach((row, index) => {
      if (row.classList.contains("hidden")) {
        this.hideEntrySummary(index);
        return;
      }

      const stockSelect = row.querySelector("select");
      const unitsInput = row.querySelector(
        "[data-statement-secondary-packaging-line-items--form-target='unitsCount']",
      );
      const priceInput = row.querySelector(
        "[data-statement-secondary-packaging-line-items--form-target='pricePerGram']",
      );

      const units = parseInt(unitsInput?.value) || 0;
      const pricePerGram = parseFloat(priceInput?.value) || 0;

      if (
        stockSelect?.selectedOptions[0] &&
        stockSelect.selectedOptions[0].value &&
        units > 0
      ) {
        const option = stockSelect.selectedOptions[0];
        const weightPerUnit =
          parseFloat(option.dataset.weightPerUnitGrams) || 0;
        const entryWeight = units * weightPerUnit;
        const entryPrice = entryWeight * pricePerGram;

        totalWeight += entryWeight;
        grandTotal += entryPrice;
        hasValidEntries = true;

        // Update per-entry total display
        const entryTotalEls = this.entryTotalTargets;
        if (entryTotalEls[index] && pricePerGram > 0) {
          entryTotalEls[index].textContent = `${symbol}${this.formatNumber(entryPrice)} ${currencyCode}`;
          entryTotalEls[index].classList.remove("hidden");
        } else if (entryTotalEls[index]) {
          entryTotalEls[index].classList.add("hidden");
        }

        this.updateEntrySummary(index, {
          units,
          packagingName: option.dataset.packagingName || "",
          cropVariety: option.dataset.cropVariety || "",
          weightPerUnit,
          entryWeight,
          entryPrice,
          pricePerGram,
          symbol,
          currencyCode,
        });
      } else {
        this.hideEntrySummary(index);
        const entryTotalEls = this.entryTotalTargets;
        if (entryTotalEls[index]) {
          entryTotalEls[index].classList.add("hidden");
        }
      }
    });

    if (this.showPricesValue) {
      this.totalPriceTarget.textContent = `${symbol}${this.formatNumber(grandTotal)} ${currencyCode}`;
    }

    // Update breakdown under total price
    if (this.hasSummaryContentTarget) {
      if (hasValidEntries && totalWeight > 0) {
        this.summaryContentTarget.classList.remove("hidden");
        this.summaryTotalWeightTarget.textContent = `${this.formatNumber(totalWeight)}g`;
      } else {
        this.summaryContentTarget.classList.add("hidden");
      }
    }
  }

  updateEntrySummary(index, data) {
    const summaries = this.entrySummaryTargets;
    if (!summaries[index]) return;

    if (data.pricePerGram > 0) {
      summaries[index].classList.remove("hidden");
    } else {
      summaries[index].classList.add("hidden");
      return;
    }

    const unitsEls = this.entrySummaryUnitsTargets;
    const packagingEls = this.entrySummaryPackagingTargets;
    const wpuEls = this.entrySummaryWeightPerUnitTargets;
    const varietyEls = this.entrySummaryVarietyTargets;
    const totalWeightEls = this.entrySummaryTotalWeightTargets;
    const priceEls = this.entrySummaryPriceTargets;

    if (unitsEls[index]) unitsEls[index].textContent = data.units;
    if (packagingEls[index])
      packagingEls[index].textContent = data.packagingName;
    if (wpuEls[index])
      wpuEls[index].textContent = this.formatNumber(data.weightPerUnit);
    if (varietyEls[index]) varietyEls[index].textContent = data.cropVariety;
    if (totalWeightEls[index])
      totalWeightEls[index].textContent = this.formatNumber(data.entryWeight);
    if (priceEls[index])
      priceEls[index].textContent = `${data.symbol}${this.formatNumber(data.entryPrice)} ${data.currencyCode}`;
  }

  hideEntrySummary(index) {
    const summaries = this.entrySummaryTargets;
    if (summaries[index]) {
      summaries[index].classList.add("hidden");
    }
  }

  updateAllAvailableUnits() {
    this.entryRowTargets.forEach((row) => {
      if (row.classList.contains("hidden")) return;
      this.updateAvailableUnitsForRow(row);
    });
  }

  updateAvailableUnitsForRow(row) {
    const stockSelect = row.querySelector("select");
    const availableDisplay = row.querySelector(
      "[data-statement-secondary-packaging-line-items--form-target='availableUnits']",
    );

    if (!stockSelect || !availableDisplay) return;

    const option = stockSelect.selectedOptions[0];
    if (option && option.value) {
      availableDisplay.textContent = option.dataset.availableUnits || "-";
    } else {
      availableDisplay.textContent = "-";
    }
  }

  stockChanged(event) {
    const row = event.target.closest(
      "[data-statement-secondary-packaging-line-items--form-target='entryRow']",
    );
    if (row) {
      this.updateAvailableUnitsForRow(row);
    }
    this.disableSelectedStocks();
    this.updateTotals();
  }

  disableSelectedStocks() {
    const visibleRows = this.entryRowTargets.filter(
      (r) => !r.classList.contains("hidden"),
    );
    const selects = visibleRows.map((r) => r.querySelector("select"));

    const selectedIds = new Set();
    selects.forEach((sel) => {
      if (sel?.value) selectedIds.add(sel.value);
    });

    selects.forEach((sel) => {
      if (!sel) return;
      const ownValue = sel.value;
      Array.from(sel.options).forEach((opt) => {
        if (!opt.value) return;
        opt.disabled = opt.value !== ownValue && selectedIds.has(opt.value);
      });
    });
  }

  currencySymbol(code) {
    const option = this.currencyTarget?.querySelector(
      `option[value="${code}"]`,
    );
    if (option) {
      return option.textContent.split(" ")[0];
    }
    return "$";
  }

  formatNumber(n) {
    return n.toLocaleString(navigator.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
